import { useCallback, useEffect, useRef, useState } from "react"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card"
import { workflowBadge } from "@/lib/workflow-queue-display"

export interface StaffQueuePage<T> {
  items: T[]
  page: number
  pageSize: number
  totalCount: number
}

export interface StaffQueueApi {
  get<T>(path: string): Promise<T>
}

export interface OwnerQueueDisplayItem {
  v2State?: string | null
  authority?: string
  label?: string
  readUnavailable?: boolean
  proofingStatus: number
  grantAmount: number
}

export interface UseStaffOwnerQueueResult<T> {
  items: T[]
  totalCount: number
  loading: boolean
  error: string | null
  invalidate: () => void
  reload: () => void
}

/**
 * Loads one bounded selected-owner queue page. The request identity is deliberately local to
 * this hook: a late owner/view/page response can never replace the currently selected result.
 */
export function useStaffOwnerQueue<T>(
  apiClient: StaffQueueApi,
  selectedUserId: string,
  queueView: string,
  page: number,
  pageSize: number,
  enabled = true,
): UseStaffOwnerQueueResult<T> {
  const requestRef = useRef(0)
  const [items, setItems] = useState<T[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const invalidate = useCallback(() => {
    requestRef.current += 1
  }, [])

  const reload = useCallback(() => {
    setReloadToken((value) => value + 1)
  }, [])

  useEffect(() => {
    if (!enabled || !selectedUserId) return

    const requestId = ++requestRef.current
    setLoading(true)
    setError(null)
    setItems([])
    setTotalCount(0)

    void apiClient.get<StaffQueuePage<T>>(
      `/api/benefitapplications/by-staff/page?userId=${encodeURIComponent(selectedUserId)}&page=${page}&pageSize=${pageSize}&view=${encodeURIComponent(queueView)}`,
    ).then((data) => {
      if (requestId !== requestRef.current) return
      setItems(data.items)
      setTotalCount(data.totalCount)
    }).catch((cause: unknown) => {
      if (requestId !== requestRef.current) return
      setError(cause instanceof Error ? cause.message : "Failed to fetch")
    }).finally(() => {
      if (requestId === requestRef.current) setLoading(false)
    })
  }, [apiClient, enabled, page, pageSize, queueView, reloadToken, selectedUserId])

  return { items, totalCount, loading, error, invalidate, reload }
}

/**
 * The selected-owner cards are intentionally page-scoped. Only the server total is a filtered
 * total; status and amount cards describe the bounded rows currently returned by the API.
 */
export function OwnerQueueSummary({ items, totalCount }: { items: OwnerQueueDisplayItem[]; totalCount: number }) {
  const certified = items.filter((item) => item.v2State === "Certified" ||
    (item.authority === "LegacyCompatibility" && item.label?.endsWith("Certified"))).length
  const pending = items.filter((item) => item.v2State === "Pending" ||
    (item.authority === "LegacyCompatibility" && item.label?.endsWith("Pending"))).length
  const amount = items.reduce((sum, item) => sum + item.grantAmount, 0)

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="owner-queue-summary">
      <Card className="@container/card">
        <CardHeader className="pb-2">
          <CardDescription className="text-xs">Filtered total</CardDescription>
          <CardTitle className="text-xl tabular-nums">{totalCount}</CardTitle>
        </CardHeader>
      </Card>
      <Card className="@container/card">
        <CardHeader className="pb-2">
          <CardDescription className="text-xs">Certified (page)</CardDescription>
          <CardTitle className="text-xl tabular-nums text-green-600">{certified}</CardTitle>
        </CardHeader>
      </Card>
      <Card className="@container/card">
        <CardHeader className="pb-2">
          <CardDescription className="text-xs">Pending (page)</CardDescription>
          <CardTitle className="text-xl tabular-nums text-amber-600">{pending}</CardTitle>
        </CardHeader>
      </Card>
      <Card className="@container/card">
        <CardHeader className="pb-2">
          <CardDescription className="text-xs">Granted (page)</CardDescription>
          <CardTitle className="text-xl tabular-nums">
            ${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </CardTitle>
        </CardHeader>
      </Card>
    </div>
  )
}

/** Renders one server-resolved item for small component harnesses without client-side slicing. */
export function OwnerQueueItemLabel({ item }: { item: OwnerQueueDisplayItem }) {
  const badge = workflowBadge(item, item.proofingStatus)
  return <Badge variant={badge.variant}>{badge.label}</Badge>
}
